import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  EmailInboxMessage,
  EmailIntakeRoute,
  EmailIntakeRouteCreate,
  EmailIntakeStatusDto,
  EmailSenderAllowlist,
  EmailSenderAllowlistCreate,
} from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const key = (companyId: string | undefined) => ['email-intake', companyId] as const;

export function useMailboxStatus(companyId: string | undefined) {
  return useQuery({
    queryKey: [...key(companyId), 'status'],
    queryFn: () => api<EmailIntakeStatusDto>(`/companies/${companyId}/email-intake/status`),
    enabled: !!companyId,
  });
}

export function useEmailRoutes(companyId: string | undefined) {
  return useQuery({
    queryKey: [...key(companyId), 'routes'],
    queryFn: () => api<EmailIntakeRoute[]>(`/companies/${companyId}/email-intake/routes`),
    enabled: !!companyId,
  });
}

export function useCreateEmailRoute(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: EmailIntakeRouteCreate) =>
      apiJson<EmailIntakeRouteCreate, EmailIntakeRoute>(
        'POST',
        `/companies/${companyId}/email-intake/routes`,
        dto,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteEmailRoute(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, void>('DELETE', `/companies/${companyId}/email-intake/routes/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useAllowlist(companyId: string | undefined) {
  return useQuery({
    queryKey: [...key(companyId), 'allowlist'],
    queryFn: () => api<EmailSenderAllowlist[]>(`/companies/${companyId}/email-intake/allowlist`),
    enabled: !!companyId,
  });
}

export function useCreateAllowlistEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: EmailSenderAllowlistCreate) =>
      apiJson<EmailSenderAllowlistCreate, EmailSenderAllowlist>(
        'POST',
        `/companies/${companyId}/email-intake/allowlist`,
        dto,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteAllowlistEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, void>('DELETE', `/companies/${companyId}/email-intake/allowlist/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useQuarantine(companyId: string | undefined) {
  return useQuery({
    queryKey: [...key(companyId), 'quarantine'],
    queryFn: () => api<EmailInboxMessage[]>(`/companies/${companyId}/email-intake/quarantine`),
    enabled: !!companyId,
  });
}

export function useReleaseMessage(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, void>(
        'POST',
        `/companies/${companyId}/email-intake/quarantine/${id}/release`,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDiscardMessage(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, void>(
        'POST',
        `/companies/${companyId}/email-intake/quarantine/${id}/discard`,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}
